import MoreVert from "@mui/icons-material/MoreVert";
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";
import { useNotification } from "app/NotificationProvider";
import AnimalsLocationRoundedFilledIcon from "common/icons/AnimalsLocationIconRoundedFilled";
import { useTranslation } from "common/locales";
import { FieldAnimalGroupActionMenu } from "features/fieldDetail";
import { moveAnimalsIn } from "features/singleCoverInput/moveAnimalsIn";
import {
  ARABLE_TYPE,
  ArableField,
  Field,
  getCurrentFieldWithAnimalsFromArray,
  useArableFields,
  useFields,
} from "model";
import { useAnimalGroup } from "model/useAnimalGroupQueries";
import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnalyticsEvents, mixpanel } from "../../common/analytics";
import { useUIStore } from "stores";
import FieldIcon from "./FieldIcon";

interface FieldsListProps {
  fieldIds?: string[];
  emptyText?: string;
}

const StyledListItem = styled(ListItem)({
  backgroundColor: "transparent",
  padding: "12px 16px",
  width: "100%",
  margin: 0,
  boxSizing: "border-box",
});

const StyledDivider = styled(Divider)({
  backgroundColor: "#f0f0f0",
  margin: "0 16px",
  width: "calc(100% - 32px)",
});

const StyledLink = styled(Link)({
  textDecoration: "none",
  color: "inherit",
  display: "flex",
  alignItems: "center",
  flex: 1,
  width: "100%",
  boxSizing: "border-box",
  "&:hover": {
    textDecoration: "none",
  },
});

const StyledList = styled(List)({
  padding: 0,
  width: "100%",
  boxSizing: "border-box",
});

export const ListItemStyled = styled("div")<{ $overgrazed?: boolean }>(
  ({ $overgrazed }) => ({
    width: "100%",
    boxSizing: "border-box",
    backgroundColor: $overgrazed ? "#FFFBEB" : "white", // daisy.50 for warning
    borderLeft: $overgrazed ? "3px solid #F59E0B" : "3px solid transparent", // daisy.500
    transition: "background-color 0.15s ease, border-color 0.15s ease",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: $overgrazed ? "#FEF3C7" : "#f5f3f9", // daisy.100 vs normal hover
    },
    "& .move-in-icon": {
      display: "none",
    },
    "@media (hover: hover)": {
      "&:hover .move-in-icon": {
        display: "block",
      },
    },
  })
);

export default function FieldsList({ fieldIds, emptyText }: FieldsListProps) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [currentFieldId, setCurrentFieldId] = useState<string | null>(null);

  // Use React Query for fields data
  const { data: regularFields = [], isLoading: fieldsLoading } = useFields();
  const { data: arableFieldsData = [], isLoading: arableFieldsLoading } =
    useArableFields();

  // Combine and filter fields based on fieldIds
  let fields = [...regularFields, ...arableFieldsData];
  if (fieldIds) {
    fields = fields.filter(({ guid }) => fieldIds.includes(guid));
  }

  const loading = fieldsLoading || arableFieldsLoading;

  // Only show loading state on initial load
  if (loading && fields.length === 0) {
    return <LoadingList />;
  }

  // Show empty state only when we have no fields and we're not loading
  if (fields.length === 0 && !loading) {
    return <EmptyList text={emptyText} />;
  }

  return (
    <StyledList disablePadding>
      {currentFieldId && (
        <FieldAnimalGroupActionMenu
          anchor={menuAnchor}
          close={closeMenu}
          fieldId={currentFieldId}
        />
      )}
      {fields.map((field: Field | ArableField, index) => (
        <div key={`field-key-${field.guid}`}>
          <FieldListItem field={field} openMenu={openMenu} />
          {index < fields.length - 1 && <StyledDivider />}
        </div>
      ))}
    </StyledList>
  );

  function openMenu(fieldId: string, anchor: HTMLButtonElement) {
    setMenuAnchor(anchor);
    setCurrentFieldId(fieldId);
  }

  function closeMenu() {
    setMenuAnchor(null);
    setCurrentFieldId(null);
  }
}

export function EmptyList({ text }: { text?: string }) {
  const { t } = useTranslation();
  return (
    <Box m={3}>
      <Typography color="textSecondary" align="center">
        {text ?? t("fieldlist.empty")}
      </Typography>
    </Box>
  );
}

export function LoadingList() {
  return (
    <List sx={{ p: 0 }}>
      {[1, 2, 3].map((i) => (
        <ListItem key={i} sx={{ px: 2, py: 1.5 }}>
          <ListItemAvatar sx={{ minWidth: 48 }}>
            <Skeleton variant="rounded" width={36} height={36} />
          </ListItemAvatar>
          <ListItemText
            primary={<Skeleton variant="text" width={120} height={20} />}
            secondary={<Skeleton variant="text" width={80} height={16} />}
          />
        </ListItem>
      ))}
    </List>
  );
}

function FieldListItem({ field, openMenu }: FieldListItemProps) {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const openMoveOutEventDialog = useUIStore(
    (state) => state.openMoveOutEventDialog
  );

  // Use React Query for all fields (needed for finding current field with animals)
  const { data: allFields = [] } = useFields();

  // Use React Query hook for animal group (only for regular fields with animalGroup)
  const animalGroupId =
    "animalGroup" in field ? field.animalGroup || undefined : undefined;
  const { data: animalGroupFromHook } = useAnimalGroup(animalGroupId);

  // Use React Query helper to find current field with animals
  const currentFieldWithAnimalsSelector = useMemo(
    () =>
      "animalGroup" in field
        ? getCurrentFieldWithAnimalsFromArray(
            allFields,
            field.animalGroup ?? undefined
          )
        : undefined,
    [allFields, field]
  );

  // Check if the field is an arable field by checking for arableType property
  const isArableField = (field: Field | ArableField): field is ArableField =>
    "arableType" in field &&
    field.arableType !== undefined &&
    Object.values(ARABLE_TYPE).includes(field.arableType);

  // A pasture field is a non-arable field that has an animal group
  const isPastureField = !isArableField(field) && field.animalGroup !== null;

  // A silage field is a non-arable field with no animal group
  const isSilageField = !isArableField(field) && field.animalGroup === null;

  const regularField = !isArableField(field) ? field : undefined;
  const arableField = isArableField(field) ? field : undefined;

  const animalGroup = regularField ? animalGroupFromHook : null;
  const currentFieldWithAnimals = regularField
    ? currentFieldWithAnimalsSelector
    : undefined;

  const overgrazed = Boolean(
    regularField &&
      animalGroup?.postGrazingTarget &&
      regularField.dryMatterNow &&
      regularField.dryMatterNow < animalGroup.postGrazingTarget
  );

  const hasAnimalsNow =
    regularField &&
    regularField.animalGroup &&
    regularField.guid === currentFieldWithAnimals?.guid;

  const canMoveIn =
    isPastureField &&
    regularField &&
    regularField.animalGroup &&
    regularField.guid !== currentFieldWithAnimals?.guid;

  const [isSendingMoveRequest, setSendingMoveRequest] = useState(false);

  const renderSecondaryText = () => {
    if (regularField) {
      const coverText = regularField.invalidatedTimestamp
        ? t("fieldlist.noDryMatterNow.text")
        : `${Math.round(regularField.dryMatterNow)} ${t(
            "fieldlist.dryMatterNow.unit"
          )}`;
      // Add "overgrazed" label if applicable
      if (overgrazed) {
        return `${coverText} · ${t("fieldview.overgrazed")}`;
      }
      return coverText;
    } else if (arableField) {
      return arableField.arableType && arableField.arableType.toLowerCase();
    }
  };

  const handleMoveInClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    setSendingMoveRequest(true);
    await onMoveIn();
    setSendingMoveRequest(false);
  };

  async function onMoveIn() {
    try {
      if (regularField && currentFieldWithAnimals) {
        openMoveOutEventDialog({
          open: true,
          newFieldWithAnimals: regularField,
          currentFieldWithAnimals,
        });
      } else if (regularField) {
        await moveAnimalsIn(
          regularField,
          currentFieldWithAnimals,
          new Date(),
          notify,
          t
        );
      }
    } catch (e) {
      console.error("Error during move-in operation:", e);
      notify(t("An error occurred while moving animals. Please try again."));
    }
  }

  const handleFieldClick = (e: React.MouseEvent) => {
    e.preventDefault();
    mixpanel.track(AnalyticsEvents.FIELD_SELECTED_FROM_LIST, {
      fieldId: field.guid,
    });
    navigate(`/field/${field.guid}`, {
      state: {
        previousPath: location.pathname,
        previousSearch: location.search,
      },
    });
  };

  const listItem = (
    <ListItemStyled $overgrazed={overgrazed}>
      <StyledListItem>
        <StyledLink to={`/field/${field.guid}`} onClick={handleFieldClick}>
          <ListItemAvatar sx={{ minWidth: 48 }}>
            {regularField ? (
              <FieldIcon
                geom={field.geom}
                dryMatterNow={regularField.dryMatterNow}
              />
            ) : (
              <FieldIcon geom={field.geom} dryMatterNow={100} />
            )}
          </ListItemAvatar>
          <ListItemText
            primary={
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 600,
                  color: "#1a1a2e",
                  fontSize: "0.9rem",
                }}
              >
                {field.name}
              </Typography>
            }
            secondary={
              <Typography
                variant="caption"
                sx={{
                  color: overgrazed ? "#B45309" : "text.secondary", // amber.700 for warning
                  fontSize: "0.75rem",
                }}
              >
                {renderSecondaryText()}
              </Typography>
            }
          />
          {hasAnimalsNow ? (
            <Tooltip title={t("fieldview.animalsOnThisField.text")}>
              <Box
                sx={{
                  marginLeft: "auto",
                  marginRight: !isSilageField && isPastureField ? "36px" : 0,
                }}
              >
                <IconButton
                  aria-haspopup="true"
                  disabled={true}
                  size="small"
                  sx={{ padding: "4px" }}
                >
                  <AnimalsLocationRoundedFilledIcon
                    htmlColor={animalGroup?.color}
                  />
                </IconButton>
              </Box>
            </Tooltip>
          ) : null}
        </StyledLink>
        <ListItemSecondaryAction>
          {!isSilageField && isPastureField && (
            <IconButton
              edge="end"
              aria-haspopup="true"
              onClick={(e) => openMenu(field.guid, e.currentTarget)}
              size="small"
            >
              <MoreVert sx={{ fontSize: 20 }} />
            </IconButton>
          )}
        </ListItemSecondaryAction>
      </StyledListItem>
    </ListItemStyled>
  );

  return listItem;
}

type FieldListItemProps = {
  field: Field | ArableField;
  openMenu: (guid: string, target: HTMLElement) => void;
};
